import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { User, Menu, X, LogOut, Settings, CalendarDays, ChevronDown } from "lucide-react";
import { SearchDialog } from "@/components/SearchDialog";
import { Button } from "@/components/ui/button";
import { CartDrawer } from "@/components/CartDrawer";
import { NewsletterPopover } from "@/components/NewsletterPopover";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CurrencySelector } from "@/components/CurrencySelector";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTranslation } from "react-i18next";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { useUserProfile } from "@/hooks/useUserProfile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
const logo = "/logo.webp";

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { user, profile } = useUserProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);


  // SEO-prioritized primary services dropdown — keyword-conscious, localized labels
  const primaryServices = [
    { title: t("nav.personalTraining"), path: "/services/personal-training" },
    { title: t("nav.sportsNutrition"), path: "/services/sports-nutrition" },
    { title: t("nav.athleticTraining"), path: "/services/athletic-performance" },
    { title: t("services.footballTraining"), path: "/services/football-training" },
    { title: t("services.performanceDiagnostic"), path: "/services/performance-diagnostic" },
    { title: t("nav.whatAreWeDoing"), path: "/areas-of-expertise" },
  ];


  const services = [
    { titleKey: "services.athleticTraining", path: "/categories/athletic-training" },
    { titleKey: "services.evaluation", path: "/services/evaluation" },
    { titleKey: "services.fatAnalysis", path: "/services/fat-analysis" },
    { titleKey: "services.footballTraining", path: "/services/football-training" },
    { titleKey: "services.fullBodyWeight", path: "/services/full-body-weight" },
    { titleKey: "services.gymTraining", path: "/services/gym-training" },
    { titleKey: "services.lactateTest", path: "/services/lactate-test" },
    { titleKey: "services.nutrition", path: "/services/nutrition" },
    { titleKey: "services.performanceDiagnostic", path: "/services/performance-diagnostic" },
    { titleKey: "services.psychology", path: "/services/psychology" },
    { titleKey: "services.vo2Max", path: "/services/vo2-max" },
  ].sort((a, b) => t(a.titleKey).localeCompare(t(b.titleKey)));

  const viewPlanOptions = [
    { titleKey: "nav.fitnessEnthusiasts", path: "/categories/normal-people" },
    { titleKey: "nav.eliteAthletes", path: "/categories/elite-athletes" },
    { titleKey: "nav.footballTraining", path: "/categories/football-training" },
    { title: "Athletic Individual Coaching", path: "/categories/athletic-training" } as { titleKey?: string; title?: string; path: string },
  ].sort((a: any, b: any) => {
    const an = (a.title ?? t(a.titleKey)) as string;
    const bn = (b.title ?? t(b.titleKey)) as string;
    return an.localeCompare(bn);
  });

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(t("auth.logoutFailed"));
    } else {
      toast.success(t("auth.logoutSuccess"));
      navigate("/");
    }
  };

  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || "U";
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0 shrink">
            <img src={logo} alt="Valorform Logo" width={48} height={48} fetchPriority="high" decoding="async" className="h-9 w-9 sm:h-12 sm:w-12 rounded-lg shrink-0" />
            <span className="text-lg sm:text-2xl font-bold text-primary transition-colors whitespace-nowrap truncate">
              Valorform
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/" className="px-4 py-2 text-primary hover:text-primary/80 transition-colors font-semibold">
                      {t('nav.home')}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <a
                      href="/#who-are-we"
                      className="px-4 py-2 text-primary hover:text-primary/80 transition-colors font-semibold cursor-pointer"
                      onClick={(e) => {
                        const element = document.getElementById('who-are-we');
                        if (element) {
                          e.preventDefault();
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      {t('nav.whoAreWe')}
                    </a>
                  </NavigationMenuLink>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-primary hover:text-primary/80 font-semibold">
                    {t('nav.whatAreWeDoing')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid w-[420px] gap-1 p-4">
                      <p className="px-2 pb-2 text-xs uppercase tracking-wider text-muted-foreground">{t("nav.primaryServices")}</p>
                      {primaryServices.map((s) => (
                        <NavigationMenuLink key={s.path} asChild>
                          <Link to={s.path} className="block px-4 py-2 rounded-md hover:bg-muted transition-colors text-foreground font-medium">
                            {s.title}
                          </Link>
                        </NavigationMenuLink>
                      ))}
                      <div className="border-t border-border mt-2 pt-2">
                        <p className="px-2 pb-2 text-xs uppercase tracking-wider text-muted-foreground">{t("nav.allServices")}</p>
                        {services.map((service) => (
                          <NavigationMenuLink key={service.titleKey} asChild>
                            <Link to={service.path} className="block px-4 py-2 rounded-md hover:bg-muted transition-colors text-foreground text-sm">
                              {t(service.titleKey)}
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/tools" className="px-4 py-2 text-primary hover:text-primary/80 transition-colors font-semibold">
                      {t('nav.tools')}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/reviews" className="px-4 py-2 text-primary hover:text-primary/80 transition-colors font-semibold">
                      {t('nav.caseStudy')}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-primary hover:text-primary/80 font-semibold">
                    {t('nav.viewPlans')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid w-[300px] gap-2 p-4">
                      {viewPlanOptions.map((option: any) => (
                        <NavigationMenuLink key={option.path} asChild>
                          <Link to={option.path} className="block px-4 py-2 rounded-md hover:bg-muted transition-colors text-foreground">
                            {option.title ?? t(option.titleKey)}
                          </Link>
                        </NavigationMenuLink>
                      ))}
                      <NavigationMenuLink asChild>
                        <Link to="/view-plans" className="block px-4 py-2 rounded-md hover:bg-muted transition-colors text-muted-foreground border-t border-border mt-2 pt-4">
                          {t('nav.viewAllPlans')}
                        </Link>
                      </NavigationMenuLink>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NewsletterPopover />
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* User Actions */}
          <div className="flex items-center gap-0.5 sm:gap-2 md:gap-3 shrink-0">
            <SearchDialog />
            <LanguageSwitcher />
            <div className="hidden md:block"><CurrencySelector /></div>
            <CartDrawer />
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" aria-label={t("nav.userAccount") || "User account"}>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || "User"} />
                      <AvatarFallback className="bg-primary/20 text-primary text-sm">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{profile?.full_name || t("nav.user")}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer">
                      <User className="h-4 w-4 mr-2" />
                      {t("nav.dashboard")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer">
                      <Settings className="h-4 w-4 mr-2" />
                      {t("nav.settings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("auth.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" aria-label={t("auth.login") || "Login"}>
                <Button variant="ghost" size="icon" className="text-primary hover:text-primary/80" aria-label={t("auth.login") || "Login"}>
                  <User className="h-5 w-5" />
                </Button>
              </Link>
            )}
            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-primary hover:text-primary/80 hover:bg-primary/10 shrink-0 h-11 w-11"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-7 w-7" strokeWidth={2.5} /> : <Menu className="h-7 w-7" strokeWidth={2.5} />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border overflow-y-auto overscroll-contain" style={{ maxHeight: "calc(100vh - 5rem)" }}>
            <div className="flex flex-col gap-2 py-4">
              <Link to="/" className={`px-4 py-2 rounded-md transition-colors ${isActive("/") ? "bg-primary/15 text-primary font-semibold border-l-4 border-primary" : "text-foreground hover:bg-muted"}`} aria-current={isActive("/") ? "page" : undefined}>
                {t('nav.home')}
              </Link>
              <div className="px-4 py-2">
                <Link to="/areas-of-expertise">
                  <p className={`text-sm font-semibold mb-2 transition-colors ${isActive("/areas-of-expertise") ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>{t('nav.whatAreWeDoing')}</p>
                </Link>
                <div className="flex flex-col gap-1 pl-4">
                  {services.map((service) => (
                    <Link key={service.titleKey} to={service.path} className={`py-1 text-sm transition-colors ${isActive(service.path) ? "text-primary font-semibold" : "text-foreground hover:text-primary"}`} aria-current={isActive(service.path) ? "page" : undefined}>
                      {t(service.titleKey)}
                    </Link>
                  ))}
                </div>
              </div>
              <Link to="/tools" className={`px-4 py-2 rounded-md transition-colors ${isActive("/tools") ? "bg-primary/15 text-primary font-semibold border-l-4 border-primary" : "text-foreground hover:bg-muted"}`} aria-current={isActive("/tools") ? "page" : undefined}>
                {t('nav.tools')}
              </Link>
              <Link to="/reviews" className={`px-4 py-2 rounded-md transition-colors ${isActive("/reviews") ? "bg-primary/15 text-primary font-semibold border-l-4 border-primary" : "text-foreground hover:bg-muted"}`} aria-current={isActive("/reviews") ? "page" : undefined}>
                {t('nav.caseStudy')}
              </Link>
              <div className="px-4 py-2">
                <p className="text-sm font-semibold text-muted-foreground mb-2">{t('nav.viewPlans')}</p>
                <div className="flex flex-col gap-1 pl-4">
                  {viewPlanOptions.map((option: any) => (
                    <Link key={option.path} to={option.path} className={`py-1 text-sm transition-colors ${isActive(option.path) ? "text-primary font-semibold" : "text-foreground hover:text-primary"}`} aria-current={isActive(option.path) ? "page" : undefined}>
                      {option.title ?? t(option.titleKey)}
                    </Link>
                  ))}
                  <Link to="/view-plans" className={`py-1 text-sm border-t border-border mt-2 pt-2 transition-colors ${isActive("/view-plans") ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary"}`} aria-current={isActive("/view-plans") ? "page" : undefined}>
                    {t('nav.viewAllPlans')}
                  </Link>
                </div>
              </div>
              {user && (
                <Collapsible className="px-4 py-2 border-t border-border">
                  <CollapsibleTrigger className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || "User"} />
                        <AvatarFallback className="bg-primary/20 text-primary">
                          {getInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <p className="text-sm font-medium">{profile?.full_name || t("nav.user")}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <div className="flex flex-col gap-1 pl-13">
                      <Link to="/dashboard" className="flex items-center gap-2 py-2 text-sm text-foreground hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>
                        <User className="h-4 w-4" />
                        {t("nav.dashboard")}
                      </Link>
                      <Link to="/settings" className="flex items-center gap-2 py-2 text-sm text-foreground hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>
                        <Settings className="h-4 w-4" />
                        {t("nav.settings")}
                      </Link>
                      <Link to="/dashboard" className="flex items-center gap-2 py-2 text-sm text-foreground hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>
                        <CalendarDays className="h-4 w-4" />
                        {t("booking.myBookings")}
                      </Link>
                      <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center gap-2 py-2 text-sm text-destructive hover:text-destructive/80 transition-colors text-left">
                        <LogOut className="h-4 w-4" />
                        {t("auth.logout")}
                      </button>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
              <div className="px-4 py-2 border-t border-border">
                <NewsletterPopover />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
